import { useState, useEffect } from "react";
import "../../Styles/export.scss";
import chroma from "chroma-js";
import { hexToHSL, hexToRgb, hslToHex } from "../Colorize/ColorUtils";
import { CloseButton } from "../ReusableComponents/PopupDefault";
import { usePopupQueue } from '../ReusableComponents/usePopupQueue';
import { useAuth } from '../../contexts/AuthContext';
import { useMembershipGuard } from '../Auth/useMembershipGuard';

const Export = ({ onClose, palette, colorName }) => {
  // Auth checks
  const { subscriptionActive } = useAuth();
  const { executeMembershipGuard } = useMembershipGuard();

  // Active output type
  const [activeOption, setActiveOption] = useState("Tailwind 3");
  const [activeSubOption, setActiveSubOption] = useState("HEX");

  const { activePopups, addPopup } = usePopupQueue();

  const [isCopied, setIsCopied] = useState(false);



  let colorNameFormatted = colorName.toLowerCase().replace(/\s+/g, "-");

  // Format HSL for display 
  const formatHSL = (color) => {
    try {
      // Check if already HSL format
      if (typeof color === 'string' && color.startsWith('hsl(')) {
        return color;
      }

      // Convert hex to HSL
      const { h, s, l } = hexToHSL(color);
      return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
    } catch (e) {
      console.error('Invalid color value for HSL conversion:', color, e);
      return 'hsl(0, 0%, 0%)';
    }
  };

  // Format RGB for display from our ColorUtils hexToRgb function
  const formatRGB = (color) => {
    try {
      // Check if already in HSL format
      if (typeof color === 'string' && color.startsWith('hsl(')) {
        // Convert HSL to RGB using chroma.js
        const hslMatch = color.match(/hsl\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*%\s*,\s*(\d+\.?\d*)\s*%\s*\)/);

        if (hslMatch) {
          const h = parseFloat(hslMatch[1]);
          const s = parseFloat(hslMatch[2]) / 100;
          const l = parseFloat(hslMatch[3]) / 100;

          // Use chroma.js to convert HSL to RGB
          const [r, g, b] = chroma.hsl(h, s, l).rgb();
          return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
        }
      }

      // If it's a hex value, use your hexToRgb function
      if (typeof color === 'string' && color.startsWith('#')) {
        const [r, g, b] = hexToRgb(color);
        return `rgb(${r}, ${g}, ${b})`;
      }

      // If we get here, try to handle it as a generic color
      const rgb = chroma(color).rgb();
      return `rgb(${Math.round(rgb[0])}, ${Math.round(rgb[1])}, ${Math.round(rgb[2])})`;
    } catch (e) {
      console.error('Invalid value for RGB conversion:', color, e);
      return 'rgb(0, 0, 0)';
    }
  };

  const formatRGBchannels = (color) => {
    try {
      // Check if already in HSL format
      if (typeof color === 'string' && color.startsWith('hsl(')) {
        // Convert HSL to RGB using chroma.js
        const hslMatch = color.match(/hsl\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*%\s*,\s*(\d+\.?\d*)\s*%\s*\)/);

        if (hslMatch) {
          const h = parseFloat(hslMatch[1]);
          const s = parseFloat(hslMatch[2]) / 100;
          const l = parseFloat(hslMatch[3]) / 100;

          // Use chroma.js to convert HSL to RGB
          const [r, g, b] = chroma.hsl(h, s, l).rgb();
          return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`;
        }
      }

      // If it's a hex value, use your hexToRgb function
      if (typeof color === 'string' && color.startsWith('#')) {
        const [r, g, b] = hexToRgb(color);
        return `${r}, ${g}, ${b}`;
      }

      // If we get here, try to handle it as a generic color
      const rgb = chroma(color).rgb();
      return `${Math.round(rgb[0])} ${Math.round(rgb[1])} ${Math.round(rgb[2])}`;
    } catch (e) {
      console.error('Invalid value for RGB conversion:', color, e);
      return 'rgb(0, 0, 0)';
    }
  };

  const formatHEX = (color) => {
    try {
      // If already a hex value
      if (typeof color === 'string' && color.startsWith('#')) {
        return color;
      }

      // If it's in HSL format
      if (typeof color === 'string' && color.startsWith('hsl(')) {
        const hslMatch = color.match(/hsl\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*%\s*,\s*(\d+\.?\d*)\s*%\s*\)/);

        if (hslMatch) {
          const h = parseFloat(hslMatch[1]);
          const s = parseFloat(hslMatch[2]) / 100;
          const l = parseFloat(hslMatch[3]) / 100;

          // Use your hslToHex conversion function
          return hslToHex(h, s, l);
        }
      }

      // For any other format, use chroma.js as fallback
      return chroma(color).hex();
    } catch (e) {
      console.error('Invalid value for HEX conversion:', color, e);
      return '#000000';
    }
  };

  const formatOKLCH = (color) => {
    try {
      // Get RGB values using chroma.js
      let rgb;

      // If already in HSL format
      if (typeof color === 'string' && color.startsWith('hsl(')) {
        const hslMatch = color.match(/hsl\(\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*%\s*,\s*(\d+\.?\d*)\s*%\s*\)/);

        if (hslMatch) {
          const h = parseFloat(hslMatch[1]);
          const s = parseFloat(hslMatch[2]) / 100;
          const l = parseFloat(hslMatch[3]) / 100;

          rgb = chroma.hsl(h, s, l).rgb();
        }
      }
      // If hex
      else if (typeof color === 'string' && color.startsWith('#')) {
        rgb = chroma(color).rgb();
      }
      // Any other format
      else {
        rgb = chroma(color).rgb();
      }

      // Normalize RGB values to 0-1 range
      const r = rgb[0] / 255;
      const g = rgb[1] / 255;
      const b = rgb[2] / 255;

      // Convert sRGB to linear RGB
      const linearR = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
      const linearG = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
      const linearB = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

      // Convert linear RGB to Oklab
      // First, convert to LMS
      const l = 0.4122214708 * linearR + 0.5363325363 * linearG + 0.0514459929 * linearB;
      const m = 0.2119034982 * linearR + 0.6806995451 * linearG + 0.1073969566 * linearB;
      const s = 0.0883024619 * linearR + 0.2817188376 * linearG + 0.6299787005 * linearB;

      // Apply nonlinear transformation to LMS
      const lCube = Math.cbrt(l);
      const mCube = Math.cbrt(m);
      const sCube = Math.cbrt(s);

      // Convert to Oklab
      const L = 0.2104542553 * lCube + 0.7936177850 * mCube - 0.0040720468 * sCube;
      const a = 1.9779984951 * lCube - 2.4285922050 * mCube + 0.4505937099 * sCube;
      const bLab = 0.0259040371 * lCube + 0.7827717662 * mCube - 0.8086757660 * sCube;

      // Convert to OKLCh
      const C = Math.sqrt(a * a + bLab * bLab);
      let h = Math.atan2(bLab, a) * 180 / Math.PI;
      if (h < 0) {
        h += 360;
      }

      // Scale L to 0-100% for display
      const lPercent = (L * 100).toFixed(2).replace(/\.00$/, '').replace(/\.0$/, '');

      // Format C as a decimal between 0 and 0.4 typically
      // Scale C properly - the values should be much smaller than in LCh
      const cValue = C.toFixed(2).replace(/\.00$/, '').replace(/\.0$/, '');

      // Format h as degrees
      const hValue = Math.round(h * 100) / 100;
      const hString = hValue.toString().replace(/\.00$/, '').replace(/\.0$/, '');

      return `oklch(${lPercent}% ${cValue} ${hString})`;
    } catch (e) {
      console.error('Invalid value for OKLCH conversion:', color, e);
      return 'oklch(0% 0 0)';
    }
  };



  // Render the appropriate output based on the active option
  const renderOutput = () => {
    // Tailwind 3
    if (activeOption === "Tailwind 3") {

      // HEX
      if (activeSubOption === "HEX") {
        return (
          `'${colorNameFormatted}': {
    '50': '${formatHEX(palette[0])}',
    '100': '${formatHEX(palette[1])}',
    '200': '${formatHEX(palette[2])}',
    '300': '${formatHEX(palette[3])}',
    '400': '${formatHEX(palette[4])}',
    '500': '${formatHEX(palette[5])}',
    '600': '${formatHEX(palette[6])}',
    '700': '${formatHEX(palette[7])}',
    '800': '${formatHEX(palette[8])}',
    '900': '${formatHEX(palette[9])}',
    '950': '${formatHEX(palette[10])}',
},`)
      }

      // HSL
      if (activeSubOption === "HSL") {
        return (
          `'${colorNameFormatted}': {
    '50': '${formatHSL(palette[0])}',
    '100': '${formatHSL(palette[1])}',
    '200': '${formatHSL(palette[2])}',
    '300': '${formatHSL(palette[3])}',
    '400': '${formatHSL(palette[4])}',
    '500': '${formatHSL(palette[5])}',
    '600': '${formatHSL(palette[6])}',
    '700': '${formatHSL(palette[7])}',
    '800': '${formatHSL(palette[8])}',
    '900': '${formatHSL(palette[9])}',
    '950': '${formatHSL(palette[10])}',
},`)
      }

      // RGB
      if (activeSubOption === "RGB") {
        return (
          `'${colorNameFormatted}': {
    '50': '${formatRGB(palette[0])}',
    '100': '${formatRGB(palette[1])}',
    '200': '${formatRGB(palette[2])}',
    '300': '${formatRGB(palette[3])}',
    '400': '${formatRGB(palette[4])}',
    '500': '${formatRGB(palette[5])}',
    '600': '${formatRGB(palette[6])}',
    '700': '${formatRGB(palette[7])}',
    '800': '${formatRGB(palette[8])}',
    '900': '${formatRGB(palette[9])}',
    '950': '${formatRGB(palette[10])}',
},`)

      }

      // OKLCH
      if (activeSubOption === "OKLCH") {
        return (
          `'${colorNameFormatted}': {
    '50': '${formatOKLCH(palette[0])}',
    '100': '${formatOKLCH(palette[1])}',
    '200': '${formatOKLCH(palette[2])}',
    '300': '${formatOKLCH(palette[3])}',
    '400': '${formatOKLCH(palette[4])}',
    '500': '${formatOKLCH(palette[5])}',
    '600': '${formatOKLCH(palette[6])}',
    '700': '${formatOKLCH(palette[7])}',
    '800': '${formatOKLCH(palette[8])}',
    '900': '${formatOKLCH(palette[9])}',
    '950': '${formatOKLCH(palette[10])}',
},`)
      }

    }

    // Tailwind 4
    if (activeOption === "Tailwind 4") {

      // HEX
      if (activeSubOption === "HEX") {
        return (
          `--color-${colorNameFormatted}-50: ${formatHEX(palette[0])};
--color-${colorNameFormatted}-100: ${formatHEX(palette[1])};
--color-${colorNameFormatted}-200: ${formatHEX(palette[2])};
--color-${colorNameFormatted}-300: ${formatHEX(palette[3])};
--color-${colorNameFormatted}-400: ${formatHEX(palette[4])};
--color-${colorNameFormatted}-500: ${formatHEX(palette[5])};
--color-${colorNameFormatted}-600: ${formatHEX(palette[6])};
--color-${colorNameFormatted}-700: ${formatHEX(palette[7])};
--color-${colorNameFormatted}-800: ${formatHEX(palette[8])};
--color-${colorNameFormatted}-900: ${formatHEX(palette[9])};
--color-${colorNameFormatted}-950: ${formatHEX(palette[10])};`)
      }

      // HSL
      if (activeSubOption === "HSL") {
        return (
          `--color-${colorNameFormatted}-50: ${formatHSL(palette[0])};
--color-${colorNameFormatted}-100: ${formatHSL(palette[1])};
--color-${colorNameFormatted}-200: ${formatHSL(palette[2])};
--color-${colorNameFormatted}-300: ${formatHSL(palette[3])};
--color-${colorNameFormatted}-400: ${formatHSL(palette[4])};
--color-${colorNameFormatted}-500: ${formatHSL(palette[5])};
--color-${colorNameFormatted}-600: ${formatHSL(palette[6])};
--color-${colorNameFormatted}-700: ${formatHSL(palette[7])};
--color-${colorNameFormatted}-800: ${formatHSL(palette[8])};
--color-${colorNameFormatted}-900: ${formatHSL(palette[9])};
--color-${colorNameFormatted}-950: ${formatHSL(palette[10])};`)
      }

      // RGB
      if (activeSubOption === "RGB") {
        return (
          `--color-${colorNameFormatted}-50: ${formatRGB(palette[0])};
--color-${colorNameFormatted}-100: ${formatRGB(palette[1])};
--color-${colorNameFormatted}-200: ${formatRGB(palette[2])};
--color-${colorNameFormatted}-300: ${formatRGB(palette[3])};
--color-${colorNameFormatted}-400: ${formatRGB(palette[4])};
--color-${colorNameFormatted}-500: ${formatRGB(palette[5])};
--color-${colorNameFormatted}-600: ${formatRGB(palette[6])};
--color-${colorNameFormatted}-700: ${formatRGB(palette[7])};
--color-${colorNameFormatted}-800: ${formatRGB(palette[8])};
--color-${colorNameFormatted}-900: ${formatRGB(palette[9])};
--color-${colorNameFormatted}-950: ${formatRGB(palette[10])};`)
      }

      // OKLCH
      if (activeSubOption === "OKLCH") {
        return (
          `--color-${colorNameFormatted}-50: ${formatOKLCH(palette[0])};
--color-${colorNameFormatted}-100: ${formatOKLCH(palette[1])};
--color-${colorNameFormatted}-200: ${formatOKLCH(palette[2])};
--color-${colorNameFormatted}-300: ${formatOKLCH(palette[3])};
--color-${colorNameFormatted}-400: ${formatOKLCH(palette[4])};
--color-${colorNameFormatted}-500: ${formatOKLCH(palette[5])};
--color-${colorNameFormatted}-600: ${formatOKLCH(palette[6])};
--color-${colorNameFormatted}-700: ${formatOKLCH(palette[7])};
--color-${colorNameFormatted}-800: ${formatOKLCH(palette[8])};
--color-${colorNameFormatted}-900: ${formatOKLCH(palette[9])};
--color-${colorNameFormatted}-950: ${formatOKLCH(palette[10])};
`)
      }
    }

    // SCSS
    if (activeOption === "SCSS") {
      // HEX
      if (activeSubOption === "HEX") {
        return (
          `$${colorNameFormatted}-50: ${formatHEX(palette[0])};
$${colorNameFormatted}-100: ${formatHEX(palette[1])};
$${colorNameFormatted}-200: ${formatHEX(palette[2])};
$${colorNameFormatted}-300: ${formatHEX(palette[3])};
$${colorNameFormatted}-400: ${formatHEX(palette[4])};
$${colorNameFormatted}-500: ${formatHEX(palette[5])};
$${colorNameFormatted}-600: ${formatHEX(palette[6])};
$${colorNameFormatted}-700: ${formatHEX(palette[7])};
$${colorNameFormatted}-800: ${formatHEX(palette[8])};
$${colorNameFormatted}-900: ${formatHEX(palette[9])};
$${colorNameFormatted}-950: ${formatHEX(palette[10])};`)
      }

      // HSL
      if (activeSubOption === "HSL") {
        return (
          `$${colorNameFormatted}-50: ${formatHSL(palette[0])};
$${colorNameFormatted}-100: ${formatHSL(palette[1])};
$${colorNameFormatted}-200: ${formatHSL(palette[2])};
$${colorNameFormatted}-300: ${formatHSL(palette[3])};
$${colorNameFormatted}-400: ${formatHSL(palette[4])};
$${colorNameFormatted}-500: ${formatHSL(palette[5])};
$${colorNameFormatted}-600: ${formatHSL(palette[6])};
$${colorNameFormatted}-700: ${formatHSL(palette[7])};
$${colorNameFormatted}-800: ${formatHSL(palette[8])};
$${colorNameFormatted}-900: ${formatHSL(palette[9])};
$${colorNameFormatted}-950: ${formatHSL(palette[10])};`)
      }

      // RGB
      if (activeSubOption === "RGB") {
        return (
          `$${colorNameFormatted}-50: ${formatRGB(palette[0])};
$${colorNameFormatted}-100: ${formatRGB(palette[1])};
$${colorNameFormatted}-200: ${formatRGB(palette[2])};
$${colorNameFormatted}-300: ${formatRGB(palette[3])};
$${colorNameFormatted}-400: ${formatRGB(palette[4])};
$${colorNameFormatted}-500: ${formatRGB(palette[5])};
$${colorNameFormatted}-600: ${formatRGB(palette[6])};
$${colorNameFormatted}-700: ${formatRGB(palette[7])};
$${colorNameFormatted}-800: ${formatRGB(palette[8])};
$${colorNameFormatted}-900: ${formatRGB(palette[9])};
$${colorNameFormatted}-950: ${formatRGB(palette[10])};`)
      }

      // RGB channels
      if (activeSubOption === "RGB channels") {
        return (
          `$${colorNameFormatted}-50: ${formatRGBchannels(palette[0])};
$${colorNameFormatted}-100: ${formatRGBchannels(palette[1])};
$${colorNameFormatted}-200: ${formatRGBchannels(palette[2])};
$${colorNameFormatted}-300: ${formatRGBchannels(palette[3])};
$${colorNameFormatted}-400: ${formatRGBchannels(palette[4])};
$${colorNameFormatted}-500: ${formatRGBchannels(palette[5])};
$${colorNameFormatted}-600: ${formatRGBchannels(palette[6])};
$${colorNameFormatted}-700: ${formatRGBchannels(palette[7])};
$${colorNameFormatted}-800: ${formatRGBchannels(palette[8])};
$${colorNameFormatted}-900: ${formatRGBchannels(palette[9])};
$${colorNameFormatted}-950: ${formatRGBchannels(palette[10])};`)
      }

      // OKLCH
      if (activeSubOption === "OKLCH") {
        return (
          `$${colorNameFormatted}-50: ${formatOKLCH(palette[0])};
$${colorNameFormatted}-100: ${formatOKLCH(palette[1])};
$${colorNameFormatted}-200: ${formatOKLCH(palette[2])};
$${colorNameFormatted}-300: ${formatOKLCH(palette[3])};
$${colorNameFormatted}-400: ${formatOKLCH(palette[4])};
$${colorNameFormatted}-500: ${formatOKLCH(palette[5])};
$${colorNameFormatted}-600: ${formatOKLCH(palette[6])};
$${colorNameFormatted}-700: ${formatOKLCH(palette[7])};
$${colorNameFormatted}-800: ${formatOKLCH(palette[8])};
$${colorNameFormatted}-900: ${formatOKLCH(palette[9])};
$${colorNameFormatted}-950: ${formatOKLCH(palette[10])};
`)
      }
    }

    // CSS
    if (activeOption === "CSS") {
      // HEX
      if (activeSubOption === "HEX") {
        return (
          `--${colorNameFormatted}-50: ${formatHEX(palette[0])};
--${colorNameFormatted}-100: ${formatHEX(palette[1])};
--${colorNameFormatted}-200: ${formatHEX(palette[2])};
--${colorNameFormatted}-300: ${formatHEX(palette[3])};
--${colorNameFormatted}-400: ${formatHEX(palette[4])};
--${colorNameFormatted}-500: ${formatHEX(palette[5])};
--${colorNameFormatted}-600: ${formatHEX(palette[6])};
--${colorNameFormatted}-700: ${formatHEX(palette[7])};
--${colorNameFormatted}-800: ${formatHEX(palette[8])};
--${colorNameFormatted}-900: ${formatHEX(palette[9])};
--${colorNameFormatted}-950: ${formatHEX(palette[10])};`)
      }

      // HSL
      if (activeSubOption === "HSL") {
        return (
          `--${colorNameFormatted}-50: ${formatHSL(palette[0]).replace(/,/g, "")};
--${colorNameFormatted}-100: ${formatHSL(palette[1]).replace(/,/g, "")};
--${colorNameFormatted}-200: ${formatHSL(palette[2]).replace(/,/g, "")};
--${colorNameFormatted}-300: ${formatHSL(palette[3]).replace(/,/g, "")};
--${colorNameFormatted}-400: ${formatHSL(palette[4]).replace(/,/g, "")};
--${colorNameFormatted}-500: ${formatHSL(palette[5]).replace(/,/g, "")};
--${colorNameFormatted}-600: ${formatHSL(palette[6]).replace(/,/g, "")};
--${colorNameFormatted}-700: ${formatHSL(palette[7]).replace(/,/g, "")};
--${colorNameFormatted}-800: ${formatHSL(palette[8]).replace(/,/g, "")};
--${colorNameFormatted}-900: ${formatHSL(palette[9]).replace(/,/g, "")};
--${colorNameFormatted}-950: ${formatHSL(palette[10]).replace(/,/g, "")};`)
      }

      // RGB
      if (activeSubOption === "RGB") {
        return (
          `--${colorNameFormatted}-50: ${formatRGB(palette[0])};
--${colorNameFormatted}-100: ${formatRGB(palette[1])};
--${colorNameFormatted}-200: ${formatRGB(palette[2])};
--${colorNameFormatted}-300: ${formatRGB(palette[3])};
--${colorNameFormatted}-400: ${formatRGB(palette[4])};
--${colorNameFormatted}-500: ${formatRGB(palette[5])};
--${colorNameFormatted}-600: ${formatRGB(palette[6])};
--${colorNameFormatted}-700: ${formatRGB(palette[7])};
--${colorNameFormatted}-800: ${formatRGB(palette[8])};
--${colorNameFormatted}-900: ${formatRGB(palette[9])};
--${colorNameFormatted}-950: ${formatRGB(palette[10])};`)
      }

      // RGB channels
      if (activeSubOption === "RGB channels") {
        return (
          `--${colorNameFormatted}-50: ${formatRGBchannels(palette[0])};
--${colorNameFormatted}-100: ${formatRGBchannels(palette[1])};
--${colorNameFormatted}-200: ${formatRGBchannels(palette[2])};
--${colorNameFormatted}-300: ${formatRGBchannels(palette[3])};
--${colorNameFormatted}-400: ${formatRGBchannels(palette[4])};
--${colorNameFormatted}-500: ${formatRGBchannels(palette[5])};
--${colorNameFormatted}-600: ${formatRGBchannels(palette[6])};
--${colorNameFormatted}-700: ${formatRGBchannels(palette[7])};
--${colorNameFormatted}-800: ${formatRGBchannels(palette[8])};
--${colorNameFormatted}-900: ${formatRGBchannels(palette[9])};
--${colorNameFormatted}-950: ${formatRGBchannels(palette[10])};`)
      }

      // OKLCH
      if (activeSubOption === "OKLCH") {
        return (
          `--${colorNameFormatted}-50: ${formatOKLCH(palette[0])};
--${colorNameFormatted}-100: ${formatOKLCH(palette[1])};
--${colorNameFormatted}-200: ${formatOKLCH(palette[2])};
--${colorNameFormatted}-300: ${formatOKLCH(palette[3])};
--${colorNameFormatted}-400: ${formatOKLCH(palette[4])};
--${colorNameFormatted}-500: ${formatOKLCH(palette[5])};
--${colorNameFormatted}-600: ${formatOKLCH(palette[6])};
--${colorNameFormatted}-700: ${formatOKLCH(palette[7])};
--${colorNameFormatted}-800: ${formatOKLCH(palette[8])};
--${colorNameFormatted}-900: ${formatOKLCH(palette[9])};
--${colorNameFormatted}-950: ${formatOKLCH(palette[10])};
`)
      }
    }

    return "Select an option from the sidebar.";
  };

  return (
    
    <div className="export-popup">

      {/* row top */}
      <div className="row row--top">
        <div className="row-item row-item--1">
          Export code
        </div>

        <div className="row-item row-item--2">
          {/* Tailwind 3 */}
          <div
            className={`row-option ${activeOption === "Tailwind 3" ? "active" : ""}`}
            onClick={() => {
              setActiveOption("Tailwind 3");
              activeSubOption === 'RGB channels' && setActiveSubOption('HEX');
            }}
          >
            <p className="mode">Tailwind 3</p>
          </div>
          {/* Tailwind 4 */}
          <div
            className={`row-option ${activeOption === "Tailwind 4" ? "active" : ""}`}
            onClick={() => {
              setActiveOption("Tailwind 4");
              activeSubOption === 'RGB channels' && setActiveSubOption('HEX');
            }}
          >
            <p className="mode">Tailwind 4</p>
          </div>
          {/* SCSS */}
          <div
            className={`row-option ${activeOption === "SCSS" ? "active" : ""}`}
            onClick={() => executeMembershipGuard(() => setActiveOption("SCSS"))}
          >
            <p className={`mode ${!subscriptionActive ? 'dynamic-lock' : ''}`}>SCSS</p>
          </div>
          {/* CSS */}
          <div
            className={`row-option ${activeOption === "CSS" ? "active" : ""}`}
            onClick={() => executeMembershipGuard(() => setActiveOption("CSS"))}
          >
            <p className={`mode ${!subscriptionActive ? 'dynamic-lock' : ''}`}>CSS</p>
          </div>
        </div>
      </div>

      {/* row bottom */}
      <div className="row row--bottom">

        <div className="row-item row-item--3">
         
          <div className="formats">
            {/* HEX */}
            <div
              className={`row-option ${activeSubOption === "HEX" ? "active" : ""}`}
              onClick={() => setActiveSubOption("HEX")}
            >
              <p className="mode">HEX</p>
            </div>

            {/* OKLCH */}
            <div
              className={`row-option ${activeSubOption === "OKLCH" ? "active" : ""}`}
              onClick={() => executeMembershipGuard(() => setActiveSubOption("OKLCH"))}
            >
              <p className={`mode ${!subscriptionActive ? 'dynamic-lock' : ''}`}>OKLCH</p>
            </div>

            {/* HSL */}
            <div
              className={`row-option ${activeSubOption === "HSL" ? "active" : ""}`}
              onClick={() => executeMembershipGuard(() => setActiveSubOption("HSL"))}
            >
              <p className={`mode ${!subscriptionActive ? 'dynamic-lock' : ''}`}>HSL</p>
            </div>

            {/* RGB */}
            <div
              className={`row-option ${activeSubOption === "RGB" ? "active" : ""}`}
              onClick={() => executeMembershipGuard(() => setActiveSubOption("RGB"))}
            >
              <p className={`mode ${!subscriptionActive ? 'dynamic-lock' : ''}`}>RGB</p>
            </div>

            {/* RGB Channels */}
            {activeOption === "CSS" || activeOption === "SCSS" ?
              (
                <div
                  className={`row-option ${activeSubOption === "RGB channels" ? "active" : ""}`}
                  onClick={() => executeMembershipGuard(() => setActiveSubOption("RGB channels"))}
                >
                  <p className={`mode ${!subscriptionActive ? 'dynamic-lock' : ''}`}>RGB channels</p>
                </div>
              )
              : (null)
            }
          </div>
    
          {/* copy */}
          <button
            className={`copy-btn svg---text pri-btn ${isCopied ? 'copied' : ''}`}
            onClick={() => {
              navigator.clipboard.writeText(renderOutput());
              addPopup(`Copied code`);

              // Add the 'copied' class
              setIsCopied(true);

              // Remove it after 2 seconds
              setTimeout(() => {
                setIsCopied(false);
              }, 1000);
            }}
          >
            <svg className="copy-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M3.7 12.7H2.8C2.32261 12.7 1.86477 12.5104 1.52721 12.1728C1.18964 11.8352 1 11.3774 1 10.9V2.8C1 2.32261 1.18964 1.86477 1.52721 1.52721C1.86477 1.18964 2.32261 1 2.8 1H10.9C11.3774 1 11.8352 1.18964 12.1728 1.52721C12.5104 1.86477 12.7 2.32261 12.7 2.8V3.7M9.1 7.3H17.2C18.1941 7.3 19 8.10589 19 9.1V17.2C19 18.1941 18.1941 19 17.2 19H9.1C8.10589 19 7.3 18.1941 7.3 17.2V9.1C7.3 8.10589 8.10589 7.3 9.1 7.3Z" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <svg className="checkmark-icon" width={18} height={18} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#ffffff">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
            {/* <span>Copy code</span> */}
            <span>{isCopied ? 'Copied' : 'Copy code'}</span>
          </button>
       
        </div>


        {/* code to copy */}
        <div className="row-item row-item--4">
          <pre className="code-block">{renderOutput()}</pre>
        </div>
      </div>


      <CloseButton onClose={onClose} />

      {/* popup */}
      {activePopups.length > 0 && (
        <div className="popup-timed-container centered">
          {activePopups.map((popup) => (
            <div key={popup.id} className="popup-timed">
              <div className="popup-timed-content">
                <div className="svg---text">
                  <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  <p>{popup.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

  );
};

export default Export;